/**
 * useCookieConsent Hook
 *
 * A custom hook to access and manage cookie consent state throughout the application.
 * Useful for conditionally rendering components based on consent status.
 *
 * @module components/Organisms/CookieConsent/useCookieConsent
 */

import { useCookies } from 'react-cookie';
import { useCallback } from 'react';

/**
 * Hook to manage cookie consent state
 *
 * @returns {Object} Cookie consent state and methods
 * @returns {boolean} hasConsent - Whether user has made a consent choice
 * @returns {boolean} analyticsEnabled - Whether analytics cookies are enabled
 * @returns {Function} resetConsent - Function to reset consent (shows banner again)
 * @returns {Function} enableAnalytics - Function to enable analytics
 * @returns {Function} disableAnalytics - Function to disable analytics
 */
export const useCookieConsent = () => {
  const [cookies, setCookie, removeCookie] = useCookies([
    'ess_cookie_consent',
    'ess_analytics_consent',
  ]);

  const hasConsent = cookies.ess_cookie_consent === 'true';
  const analyticsEnabled = cookies.ess_analytics_consent === 'true';

  /**
   * Reset consent - removes cookies and forces banner to show again
   */
  const resetConsent = useCallback(() => {
    removeCookie('ess_cookie_consent', { path: '/' });
    removeCookie('ess_analytics_consent', { path: '/' });

    // Reload page to show banner
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  }, [removeCookie]);

  /**
   * Enable analytics cookies
   */
  const enableAnalytics = useCallback(() => {
    const expiryDate = new Date();
    expiryDate.setMonth(expiryDate.getMonth() + 6);

    const options = {
      path: '/',
      expires: expiryDate,
      sameSite: 'lax',
    };

    setCookie('ess_cookie_consent', 'true', options);
    setCookie('ess_analytics_consent', 'true', options);
  }, [setCookie]);

  /**
   * Disable analytics cookies
   */
  const disableAnalytics = useCallback(() => {
    const expiryDate = new Date();
    expiryDate.setMonth(expiryDate.getMonth() + 6);

    const options = {
      path: '/',
      expires: expiryDate,
      sameSite: 'lax',
    };

    setCookie('ess_cookie_consent', 'true', options);
    setCookie('ess_analytics_consent', 'false', options);
  }, [setCookie]);

  return {
    hasConsent,
    analyticsEnabled,
    resetConsent,
    enableAnalytics,
    disableAnalytics,
  };
};

export default useCookieConsent;
